import { useContext, useState } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { useSelector } from "react-redux";
import {
  AFFILIATION_FEATURE_FLAG,
  LARGE_MOBILE,
  MOBILE_MENU,
  MOBILE_SEARCH,
} from "common/constants";
import {
  PAYOUTS_MOBILE,
  TAB_AND_BUTTONS_IN_ROW,
} from "features/affiliation/common/constants";
import { AffiliationTabs } from "features/affiliation/common/types";
import useMedia from "utils/hooks/useMedia";
import { affiliationService } from "app/services/affiliation.service";
import { displayResultMessage } from "utils/misc";
import { copyCodeOrLinkToClipboard } from "features/affiliation/common/utils";
import usePayouts from "features/affiliation/common/hooks/usePayouts";
import { sharedLib } from "@shared/services/shared.service";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { useTabToDisplay } from "features/affiliation/common/hooks/misc.ts";

const useAffiliationPageWrapper = (tab?: AffiliationTabs) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const verySmallMobile = useMedia([`(max-width: 375px)`], [true], false);
  const isPayoutsMobile = useMedia(
    [`(max-width: ${PAYOUTS_MOBILE})`],
    [true],
    false
  );
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { analytics } = useContext(AnalyticsContext);
  const tabToDisplay = useTabToDisplay({});
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const hasFeatureFlag = user?.hasFeatureFlag(AFFILIATION_FEATURE_FLAG);
  const isAdmin = user?.is_admin;
  const showManagePayoutMethodsButton =
    isPayoutsMobile && tab === AffiliationTabs.PAYOUTS;

  const {
    payoutMethodsQuery,
    isCreatePayoutModalVisible,
    onCreatePayoutClick,
    closeCreatePayoutModal,
    hasPayoutMethod,
  } = usePayouts();

  const { data: balance } = affiliationService.useGetWithdrawableFundsQuery();
  const [getOrCreatePromoCode, { isLoading: getPromoCodeLoading }] =
    affiliationService.useGetOrCreatePromoCodeMutation();
  const [getOrCreateReferralLink, { isLoading: getReferralLinkLoading }] =
    affiliationService.useGetOrCreateReferralLinkMutation();

  const [isCreateLinkModalVisible, setIsCreateLinkModalVisible] =
    useState(false);
  const [isPayoutMethodsModalVisible, setIsPayoutMethodsModalVisible] =
    useState(false);
  const [promoCode, setPromoCode] = useState<string>();
  const [referralLink, setReferralLink] = useState<string>();

  const getTabsAndButtonsInRowScreenSize = () => {
    if (!isAdmin) return hasFeatureFlag ? LARGE_MOBILE : MOBILE_SEARCH;
    else if (!hasFeatureFlag) return MOBILE_MENU;
    return TAB_AND_BUTTONS_IN_ROW;
  };

  const tabsAndButtonsInRow = useMedia(
    [`(min-width: ${getTabsAndButtonsInRowScreenSize()})`],
    [true],
    false
  );

  const onCopyCodeClick = (
    endpoint: any,
    code: string | undefined,
    setCode: (code: string) => void,
    isReferralLink: boolean
  ) => {
    analytics?.affiliationCopyPromoCodeClicked({});
    if (!code) {
      displayResultMessage(endpoint().unwrap()).then((response) => {
        copyCodeOrLinkToClipboard(response.code, isReferralLink);
        setCode(response.code);
      });
    } else {
      copyCodeOrLinkToClipboard(code, isReferralLink);
    }
  };

  const copyPromoCode = () => {
    onCopyCodeClick(getOrCreatePromoCode, promoCode, setPromoCode, false);
  };

  const createOrCopyReferralLink = () => {
    hasFeatureFlag
      ? openCreateLinkModal()
      : onCopyCodeClick(
          getOrCreateReferralLink,
          referralLink,
          setReferralLink,
          true
        );
  };

  const openCreateLinkModal = () => {
    setIsCreateLinkModalVisible(true);
    analytics?.affiliationNewReferralLinkClicked({});
  };

  const closeCreateLinkModal = () => {
    setIsCreateLinkModalVisible(false);
  };

  const removeTab = (tabs: AffiliationTabs[], tabToRemove: AffiliationTabs) => {
    return tabs.filter((tab) => tab !== tabToRemove);
  };

  const getTabs = () => {
    let tabs = Object.values(AffiliationTabs);
    if (!isAdmin) tabs = removeTab(tabs, AffiliationTabs.EXISTING_USERS);
    if (!hasFeatureFlag) tabs = removeTab(tabs, AffiliationTabs.REFERRAL_LINKS);
    return tabs;
  };

  const tabItems = getTabs().map((tab) => {
    return {
      label: tabToDisplay[tab],
      key: tabToDisplay[tab],
      tab,
    };
  });

  return {
    isMobile,
    verySmallMobile,
    tabsAndButtonsInRow,
    isAdmin,
    hasFeatureFlag,
    getPromoCodeLoading,
    getReferralLinkLoading,
    isCreateLinkModalVisible,
    copyPromoCode,
    createOrCopyReferralLink,
    closeCreateLinkModal,
    tabItems,
    balance,
    payoutMethodsQuery,
    isCreatePayoutModalVisible,
    onCreatePayoutClick,
    closeCreatePayoutModal,
    hasPayoutMethod,
    isPayoutMethodsModalVisible,
    setIsPayoutMethodsModalVisible,
    showManagePayoutMethodsButton,
    isBottomNavigator,
  };
};

export default useAffiliationPageWrapper;
